import { useEffect, useState } from "react";
import { formatDate } from "../../utils/datetime";
import { useNavigate, useParams } from "react-router-dom";
import ManagementAPI from "./managementAPI";
import { Alert, Modal, TextField, SelectField } from "./crudkit";
import { useAuth } from "../../Context/AuthContext";

const statusClass = (s) => (s === "ACTIVE" ? "ok" : s === "SUSPENDED" ? "danger" : "neutral");
const TABS = ["Overview", "Users", "Roles", "Usage"];

export default function OrgDetail() {
    const { orgId } = useParams();
    const navigate  = useNavigate();
    const { enterImpersonation } = useAuth();
    const [impersonating, setImpersonating] = useState(false);
    const [org, setOrg]         = useState(null);
    const [tab, setTab]         = useState("Overview");
    const [loading, setLoading] = useState(true);
    const [error, setError]     = useState("");
    const [actionMsg, setActionMsg] = useState("");

    const [users, setUsers]   = useState(null);
    const [roles, setRoles]   = useState(null);
    const [stats, setStats]   = useState(null);
    const [planHistory, setPlanHistory] = useState([]);
    // Per-org billing overrides (null = inherit global)
    const [bo, setBo]         = useState(null);
    const [boSaving, setBoSaving] = useState(false);
    const [boMsg, setBoMsg]   = useState("");
    const [tabLoading, setTabLoading] = useState(false);
    const [usersPage, setUsersPage]   = useState(1);
    const [editOpen, setEditOpen]     = useState(false);
    const [oform, setOform]           = useState({});
    const [osaving, setOsaving]       = useState(false);
    const [oerr, setOerr]             = useState("");
    const setOf = (k, v) => setOform(f => ({ ...f, [k]: v }));

    const loadUsers = async () => { setTabLoading(true); try { setUsers(await ManagementAPI.getOrgUsers(orgId, usersPage)); } catch (e) { setError(e.message); } finally { setTabLoading(false); } };
    const loadRoles = async () => { setTabLoading(true); try { setRoles(await ManagementAPI.getOrgRoles(orgId)); } catch (e) { setError(e.message); } finally { setTabLoading(false); } };
    const loadStats = async () => { setTabLoading(true); try { setStats(await ManagementAPI.getOrgStats(orgId)); } catch (e) { setError(e.message); } finally { setTabLoading(false); } };

    useEffect(() => {
        ManagementAPI.getOrg(orgId)
            .then(data => { setOrg(data); setBo(data.billing_overrides || null); setLoading(false); })
            .catch(err => {
                if (err.message?.includes("401")) navigate("/management/login");
                setError(err.message); setLoading(false);
            });
        ManagementAPI.getOrgPlanHistory(orgId)
            .then(d => setPlanHistory(d.history || []))
            .catch(() => setPlanHistory([]));
    }, [orgId]);

    useEffect(() => {
        if (!org) return;
        if (tab === "Users") loadUsers();
        if (tab === "Roles") loadRoles();
        if (tab === "Usage") loadStats();
    }, [tab, org]);

    useEffect(() => { if (tab === "Users") loadUsers(); }, [usersPage]);

    // ── Per-org billing overrides ──
    const boOn = bo !== null;
    const setBOField = (k, v) => setBo(prev => ({ ...(prev || {}), [k]: v }));
    const toggleBO = (on) => setBo(on ? (bo || { taxes: [], discount_type: "none", discount_value: 0, tax_inclusive: false }) : null);
    const boAddTax = () => setBo(p => ({ ...(p || {}), taxes: [...((p && p.taxes) || []), { name: "", rate_pct: 0 }] }));
    const boRemoveTax = (i) => setBo(p => ({ ...p, taxes: (p.taxes || []).filter((_, idx) => idx !== i) }));
    const boSetTax = (i, k, v) => setBo(p => ({ ...p, taxes: (p.taxes || []).map((t, idx) => idx === i ? { ...t, [k]: v } : t) }));
    const saveBo = async () => {
        setBoSaving(true); setBoMsg("");
        try {
            let body;
            if (bo === null) body = { billing_overrides: null };
            else body = { billing_overrides: {
                ...bo,
                discount_value: Number(bo.discount_value) || 0,
                ...(bo.payment_grace_days != null ? { payment_grace_days: Number(bo.payment_grace_days) || 0 } : {}),
                taxes: (bo.taxes || []).filter(t => (t.name || "").trim()).map(t => ({ name: t.name.trim(), rate_pct: Number(t.rate_pct) || 0 })),
            } };
            const saved = await ManagementAPI.updateOrg(orgId, body);
            setBo(saved.billing_overrides || null);
            setBoMsg("Billing overrides saved.");
        } catch (e) { setBoMsg(e.message); } finally { setBoSaving(false); }
    };

    const handleSuspend = async () => {
        if (!confirm("Suspend this organization? All logins will be blocked.")) return;
        await ManagementAPI.suspendOrg(orgId); setActionMsg("Organization suspended."); setOrg(o => ({ ...o, status: "SUSPENDED" }));
    };
    const handleActivate = async () => {
        await ManagementAPI.activateOrg(orgId); setActionMsg("Organization activated."); setOrg(o => ({ ...o, status: "ACTIVE" }));
    };

    // "Act as org": mint a tenant session for this org and open the real tenant
    // dashboard (Intents, Knowledge Base, Flow Builder, Channels, Billing, …).
    const handleImpersonate = async () => {
        setImpersonating(true); setError("");
        try {
            const data = await ManagementAPI.impersonateOrg(orgId);
            enterImpersonation(data);
            navigate("/Dashboard");
        } catch (e) {
            setError(e.message || "Could not act as this organization.");
            setImpersonating(false);
        }
    };

    const openOrgEdit = () => {
        setOform({
            name: org.name || "", slug: org.slug || "", owner_email: org.owner_email || "",
            website: org.website || "", industry: org.industry || "",
            timezone: org.timezone || "UTC", status: org.status || "ACTIVE",
        });
        setOerr(""); setEditOpen(true);
    };
    const saveOrgEdit = async () => {
        setOsaving(true); setOerr("");
        try {
            await ManagementAPI.updateOrg(orgId, {
                name: oform.name.trim(), slug: oform.slug.trim(), owner_email: oform.owner_email.trim(),
                website: oform.website.trim(), industry: oform.industry.trim(),
                timezone: (oform.timezone || "").trim(), status: oform.status,
            });
            setOrg(await ManagementAPI.getOrg(orgId));
            setEditOpen(false); setActionMsg("Organization updated.");
        } catch (e) { setOerr(e.message); } finally { setOsaving(false); }
    };

    if (loading) return <div className="mg-loading">Loading…</div>;
    if (error && !org) return <div className="mg-alert error" style={{ justifyContent: "flex-start" }}>{error}</div>;

    return (
        <div>
            <div className="mg-title-row">
                <button className="mg-btn mg-btn-ghost mg-btn-sm" onClick={() => navigate("/management/dashboard")}>← Organizations</button>
                <h1 className="mg-h1" style={{ marginLeft: 4 }}>{org?.name}</h1>
                <span className={`mg-badge ${statusClass(org?.status)}`}>{org?.status}</span>
            </div>

            {actionMsg && <Alert type="success" msg={actionMsg} onClose={() => setActionMsg("")} />}

            <div className="mg-tabs">
                {TABS.map(t => (
                    <button key={t} className={`mg-tab ${tab === t ? "active" : ""}`} onClick={() => setTab(t)}>{t}</button>
                ))}
            </div>

            {tab === "Overview" && (
                <div>
                    <div className="mg-card mg-card-pad" style={{ marginBottom: 16 }}>
                        <h3 className="mg-card-title">Organization Details</h3>
                        <div className="mg-fieldgrid">
                            <Def label="ID" value={org.id} />
                            <Def label="Name" value={org.name} />
                            <Def label="Slug" value={<span className="mg-mono">{org.slug}</span>} />
                            <Def label="Owner Email" value={org.owner_email} />
                            <Def label="Status" value={<span className={`mg-badge ${statusClass(org.status)}`}>{org.status}</span>} />
                            <Def label="Plan" value={org.plan_id || "—"} />
                            <Def label="Members" value={org.member_count} />
                            <Def label="Trial Ends" value={org.trial_ends_at ? formatDate(org.trial_ends_at) : "—"} />
                            <Def label="Created" value={formatDate(org.created_at)} />
                            <Def label="Industry" value={org.industry || "—"} />
                            <Def label="Timezone" value={org.timezone || "—"} />
                            <Def label="Website" value={org.website || "—"} />
                        </div>
                    </div>

                    {org.subscription && (
                        <div className="mg-card mg-card-pad" style={{ marginBottom: 16 }}>
                            <h3 className="mg-card-title">Subscription</h3>
                            <div className="mg-fieldgrid">
                                <Def label="Plan Status" value={org.subscription.status} />
                                <Def label="Billing Cycle" value={org.subscription.billing_cycle} />
                            </div>
                        </div>
                    )}

                    <div className="mg-card mg-card-pad" style={{ marginBottom: 16 }}>
                        <h3 className="mg-card-title">Plan History</h3>
                        {planHistory.length === 0 ? (
                            <div className="mg-empty">No plan changes recorded yet.</div>
                        ) : (
                            <div className="mg-table-wrap">
                                <table className="mg-table">
                                    <thead>
                                        <tr>
                                            <th>S.No</th><th>When</th><th>Change</th><th>Cycle</th>
                                            <th>Status</th><th>By</th><th>Reason</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {planHistory.map((h, i) => (
                                            <tr key={h.id}>
                                                <td className="mg-td-muted mg-num">{i + 1}</td>
                                                <td>{h.created_at ? formatDate(h.created_at) : "—"}</td>
                                                <td>{h.from_plan ? `${h.from_plan} → ${h.to_plan}` : `New: ${h.to_plan}`}</td>
                                                <td>{h.billing_cycle}</td>
                                                <td><span className="mg-badge neutral">{h.status}</span></td>
                                                <td>{h.changed_by || "—"}</td>
                                                <td>{h.reason || "—"}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>

                    {/* ── Billing overrides (per-org tax/discount) ── */}
                    <div className="mg-card mg-card-pad" style={{ marginBottom: 16 }}>
                        <div className="mg-page-head" style={{ marginBottom: 8 }}>
                            <div>
                                <h3 className="mg-card-title" style={{ marginBottom: 2 }}>Billing Overrides</h3>
                                <p className="mg-sub">{boOn ? "This org uses custom tax/discount instead of the platform defaults." : "Inheriting the platform billing settings."}</p>
                            </div>
                            <button className="mg-btn mg-btn-primary mg-btn-sm" onClick={saveBo} disabled={boSaving}>{boSaving ? "Saving…" : "Save"}</button>
                        </div>
                        {boMsg && <div className="mg-alert" style={{ justifyContent: "flex-start", marginBottom: 10 }}>{boMsg}</div>}
                        <label className="mg-check" style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 8 }}>
                            <input type="checkbox" checked={boOn} onChange={e => toggleBO(e.target.checked)} />
                            <span>Override global billing settings for this organization</span>
                        </label>
                        {boOn && (
                            <>
                                <h4 className="mg-form-section">Taxes</h4>
                                {((bo && bo.taxes) || []).map((t, i) => (
                                    <div key={i} style={{ display: "flex", gap: 8, alignItems: "flex-end", marginBottom: 8 }}>
                                        <div style={{ flex: 2 }}><TextField label={i === 0 ? "Name" : ""} value={t.name} onChange={v => boSetTax(i, "name", v)} placeholder="GST" /></div>
                                        <div style={{ flex: 1 }}><TextField label={i === 0 ? "Rate %" : ""} type="number" value={t.rate_pct} onChange={v => boSetTax(i, "rate_pct", v)} placeholder="18" /></div>
                                        <button className="mg-btn mg-btn-ghost mg-btn-sm" style={{ marginBottom: 2 }} onClick={() => boRemoveTax(i)}>Remove</button>
                                    </div>
                                ))}
                                <button className="mg-btn mg-btn-ghost mg-btn-sm" onClick={boAddTax}>+ Add Tax</button>
                                <div style={{ marginTop: 10 }}>
                                    <label className="mg-check" style={{ display: "flex", gap: 8, alignItems: "center" }}>
                                        <input type="checkbox" checked={!!(bo && bo.tax_inclusive)} onChange={e => setBOField("tax_inclusive", e.target.checked)} />
                                        <span>Prices are tax-inclusive</span>
                                    </label>
                                </div>
                                <h4 className="mg-form-section">Discount</h4>
                                <SelectField label="Discount type" value={(bo && bo.discount_type) || "none"} onChange={v => setBOField("discount_type", v)}
                                    options={[{ value: "none", label: "None" }, { value: "percent", label: "Percentage (%)" }, { value: "fixed", label: "Fixed amount (cents)" }]} />
                                {bo && bo.discount_type && bo.discount_type !== "none" && (
                                    <TextField label={bo.discount_type === "percent" ? "Discount %" : "Discount cents"} type="number" value={bo.discount_value || 0} onChange={v => setBOField("discount_value", v)} />
                                )}
                            </>
                        )}
                    </div>

                    <div className="mg-head-actions">
                        {org.status === "ACTIVE" && (
                            <button className="mg-btn mg-btn-primary" onClick={handleImpersonate} disabled={impersonating}>
                                {impersonating ? "Opening…" : "Manage as this org →"}
                            </button>
                        )}
                        <button className="mg-btn mg-btn-ghost" onClick={openOrgEdit}>Edit Details</button>
                        {org.status === "ACTIVE"
                            ? <button className="mg-btn mg-btn-danger" onClick={handleSuspend}>Suspend Organization</button>
                            : <button className="mg-btn mg-btn-ghost" style={{ borderColor: "rgba(23,178,106,.4)", color: "var(--mg-ok)" }} onClick={handleActivate}>Activate Organization</button>}
                        <button className="mg-btn mg-btn-ghost" onClick={() => navigate(`/management/audit-logs?org_id=${orgId}`)}>View Audit Logs</button>
                    </div>
                </div>
            )}

            {tab === "Users" && (
                <div className="mg-card">
                    {tabLoading ? <div className="mg-loading">Loading…</div> : users && (
                        <>
                            <div className="mg-table-wrap">
                                <table className="mg-table">
                                    <thead><tr>{["S.No", "Name", "Email", "Role", "Status", "Joined"].map(h => <th key={h}>{h}</th>)}</tr></thead>
                                    <tbody>
                                        {users.users.length === 0 && <tr><td colSpan={6} className="mg-empty">No users</td></tr>}
                                        {users.users.map((u, i) => (
                                            <tr key={u.id}>
                                                <td className="mg-td-muted mg-num">{(usersPage - 1) * 20 + i + 1}</td>
                                                <td className="mg-td-strong">{u.fullname}</td>
                                                <td className="mg-td-muted">{u.email}</td>
                                                <td className="mg-td-muted">{u.role_name || u.role_id || "—"}</td>
                                                <td><span className={`mg-badge ${statusClass(u.status)}`}>{u.status}</span></td>
                                                <td className="mg-td-muted mg-num">{formatDate(u.created_at)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            <div className="mg-pagination" style={{ padding: "12px 16px" }}>
                                <button className="mg-pagebtn" onClick={() => setUsersPage(p => Math.max(1, p - 1))} disabled={usersPage === 1}>← Prev</button>
                                <span className="mg-pageinfo">Page {usersPage} · {users.total} total</span>
                                <button className="mg-pagebtn" onClick={() => setUsersPage(p => p + 1)} disabled={users.users.length < 20}>Next →</button>
                            </div>
                        </>
                    )}
                </div>
            )}

            {tab === "Roles" && (
                <div className="mg-card">
                    {tabLoading ? <div className="mg-loading">Loading…</div> : roles && (
                        <div className="mg-table-wrap">
                            <table className="mg-table">
                                <thead><tr>{["S.No", "Name", "Scope", "Permissions", "Status"].map(h => <th key={h}>{h}</th>)}</tr></thead>
                                <tbody>
                                    {roles.roles.length === 0 && <tr><td colSpan={5} className="mg-empty">No roles</td></tr>}
                                    {roles.roles.map((r, i) => (
                                        <tr key={r.id}>
                                            <td className="mg-td-muted mg-num">{i + 1}</td>
                                            <td className="mg-td-strong">{r.name}</td>
                                            <td className="mg-td-muted">{r.organization_id ? "Org-specific" : "System-wide"}</td>
                                            <td className="mg-td-muted mg-num">{r.permission_count} menus</td>
                                            <td><span className={`mg-badge ${statusClass((r.status || "").toUpperCase())}`}>{r.status}</span></td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            )}

            {tab === "Usage" && (
                <div>
                    {tabLoading ? <div className="mg-loading">Loading…</div> : stats && (
                        <>
                            <div className="mg-stat-grid">
                                <Stat label="Members" value={stats.member_count} />
                                <Stat label="Conversations (30d)" value={stats.total_conversations.toLocaleString()} tone="violet" />
                                <Stat label="Messages (30d)" value={stats.total_messages.toLocaleString()} tone="ok" />
                            </div>
                            <div className="mg-card mg-card-pad">
                                <h3 className="mg-card-title">Daily Usage — Last 30 Days</h3>
                                <div className="mg-table-wrap">
                                    <table className="mg-table">
                                        <thead><tr>{["S.No", "Date", "Conversations", "Messages", "API Calls"].map(h => <th key={h}>{h}</th>)}</tr></thead>
                                        <tbody>
                                            {stats.daily_records.length === 0 && <tr><td colSpan={5} className="mg-empty">No usage data</td></tr>}
                                            {stats.daily_records.map((r, i) => (
                                                <tr key={i}>
                                                    <td className="mg-td-muted mg-num">{i + 1}</td>
                                                    <td className="mg-num" style={{ whiteSpace: "nowrap" }}>{r.date}</td>
                                                    <td className="mg-num">{r.conversations.toLocaleString()}</td>
                                                    <td className="mg-num">{r.messages.toLocaleString()}</td>
                                                    <td className="mg-num mg-td-muted">{r.api_calls.toLocaleString()}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </>
                    )}
                </div>
            )}

            {editOpen && (
                <Modal
                    title={`Edit Organization: ${org?.name}`}
                    onClose={() => setEditOpen(false)} onSave={saveOrgEdit} saving={osaving}
                    error={oerr} saveLabel="Save Changes"
                >
                    <TextField label="Name *" value={oform.name} onChange={v => setOf("name", v)} placeholder="Acme Inc." />
                    <TextField label="Slug *" value={oform.slug} onChange={v => setOf("slug", v)} placeholder="acme-inc" />
                    <TextField label="Owner Email *" type="email" value={oform.owner_email} onChange={v => setOf("owner_email", v)} placeholder="owner@acme.com" />
                    <TextField label="Website" value={oform.website} onChange={v => setOf("website", v)} placeholder="https://acme.com" />
                    <TextField label="Industry" value={oform.industry} onChange={v => setOf("industry", v)} placeholder="SaaS" />
                    <TextField label="Timezone" value={oform.timezone} onChange={v => setOf("timezone", v)} placeholder="UTC" />
                    <SelectField label="Status" value={oform.status} onChange={v => setOf("status", v)} options={["ACTIVE", "SUSPENDED"]} />
                </Modal>
            )}
        </div>
    );
}

const Def = ({ label, value }) => (
    <div>
        <div className="mg-def-label">{label}</div>
        <div className="mg-def-value">{value}</div>
    </div>
);

const Stat = ({ label, value, tone }) => (
    <div className={`mg-stat ${tone || ""}`}>
        <div className="mg-stat-label">{label}</div>
        <div className="mg-stat-value">{value}</div>
    </div>
);
